import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  CircularProgress,
  IconButton,
  Divider
} from '@mui/material';
import { Close, Download } from '@mui/icons-material';
import { ExportContactsData } from '../data/useExportContacts';

export interface ExportContactsDialogProps {
  data: ExportContactsData;
}

export const ExportContactsDialog = ({ data }: ExportContactsDialogProps) => {
  const {
    isOpen,
    isExporting,
    selectedFields,
    exportAllMulti,
    contactsToExport,
    close,
    toggleField,
    setExportAllMulti,
    exportableFields,
    doExport
  } = data;

  if (!isOpen) return null;

  const isAllSelected = selectedFields.length === exportableFields.length;

  const toggleAll = () => {
    if (isAllSelected) {
      // Clear all (or maybe leave just 1 so it doesn't break, but let's clear all)
      data.toggleField('CLEAR_ALL_HACK'); // You'd need a clearAll function in the hook ideally, or just:
      exportableFields.forEach(f => {
        if (selectedFields.includes(f.value)) toggleField(f.value);
      });
    } else {
      exportableFields.forEach(f => {
        if (!selectedFields.includes(f.value)) toggleField(f.value);
      });
    }
  };

  return (
    <Dialog open={isOpen} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <Download color="primary" /> Экспорт контактов
        </Typography>
        <IconButton onClick={close} size="small" disabled={isExporting}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Typography variant="body2" color="textSecondary">
            Будет экспортировано контактов: <strong>{contactsToExport.length}</strong>
          </Typography>

          {/* Fields Selection */}
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2">Колонки для экспорта</Typography>
              <Button size="small" onClick={toggleAll} sx={{ textTransform: 'none' }}>
                {isAllSelected ? 'Снять все' : 'Выбрать все'}
              </Button>
            </Box>
            
            <Box 
              sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 1,
                bgcolor: 'background.default',
                p: 2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              {exportableFields.map(field => (
                <FormControlLabel
                  key={field.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={selectedFields.includes(field.value)}
                      onChange={() => toggleField(field.value)}
                    />
                  }
                  label={<Typography variant="body2">{field.label}</Typography>}
                />
              ))}
            </Box>
          </Box>

          <Divider />

          {/* Multiple items configuration (Emails / Phones) */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Как экспортировать Телефоны и Email'ы?
            </Typography>
            <RadioGroup
              value={exportAllMulti ? 'all' : 'first'}
              onChange={(e) => setExportAllMulti(e.target.value === 'all')}
            >
              <FormControlLabel 
                value="first" 
                control={<Radio size="small" />} 
                label={<Typography variant="body2">Только основной (первый) номер / адрес</Typography>} 
              />
              <FormControlLabel 
                value="all" 
                control={<Radio size="small" />} 
                label={<Typography variant="body2">Выгружать все (через запятую)</Typography>} 
              />
            </RadioGroup>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} color="inherit" disabled={isExporting}>
          Отмена
        </Button>
        <Button
          onClick={doExport}
          variant="contained"
          color="primary"
          disabled={isExporting || selectedFields.length === 0}
          startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <Download />}
        >
          {isExporting ? 'Формирование файла...' : 'Скачать Excel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
